/**
 * Scoring Interview Screen
 *
 * The founder-facing scoring conversation: a warm, resumable mentor chat that
 * gathers the evidence behind the score. Not a form — a natural conversation.
 * Backed by 88-01's `useInterview` data layer via `InterviewContainer`.
 */

import React from 'react';
import { View, Text, ActivityIndicator } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, RefreshCw } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { AppLayout } from '@/components/AppLayout';
import { InterviewContainer } from '@/components/interview/InterviewContainer';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useProfileStore } from '@/lib/state/profile-store';
import { useFounderStartup, founderStartupKeys } from '@/lib/interview/use-founder-startup';

/** Shared full-bleed frame so every gated state matches the interview chrome. */
function InterviewStateFrame({ children }: { children: React.ReactNode }) {
  const colors = useThemeColors();
  return (
    <AppLayout title="Tell us about your startup">
      <View className="flex-1" style={{ backgroundColor: colors.surface.primary }}>
        <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1">
          <View className="flex-1 items-center justify-center px-8">{children}</View>
        </SafeAreaView>
      </View>
    </AppLayout>
  );
}

export default function InterviewScreen() {
  const colors = useThemeColors();
  const router = useRouter();
  const queryClient = useQueryClient();

  // Resolve the REAL startup id from the DB (RLS-scoped), not the possibly
  // simulated/id-less Zustand profile — see use-founder-startup.ts.
  const { startupId, isLoading, error } = useFounderStartup();
  // The store profile is still a fine source for the display name (cosmetic).
  const startupProfile = useProfileStore((s) => s.startupProfile);
  const startupName = startupProfile?.name ?? 'your startup';

  const handleClose = () => router.back();

  // Loading → resolving the founder's startup row. A spinner, never a
  // silently-disabled composer.
  if (isLoading) {
    return (
      <InterviewStateFrame>
        <ActivityIndicator size="large" color={colors.primary[600]} />
        <Text style={{ color: colors.text.tertiary }} className="text-sm text-center mt-4">
          Getting things ready…
        </Text>
      </InterviewStateFrame>
    );
  }

  // Error → the resolution query failed. Recoverable: offer a retry.
  if (error) {
    return (
      <InterviewStateFrame>
        <View
          className="w-20 h-20 rounded-full items-center justify-center mb-4"
          style={{ backgroundColor: colors.amber.light }}
        >
          <AlertCircle size={40} color={colors.warning} />
        </View>
        <Text style={{ color: colors.text.primary }} className="text-xl font-bold text-center mb-2">
          Couldn't load your startup
        </Text>
        <Text
          style={{ color: colors.text.tertiary }}
          className="text-sm text-center leading-relaxed mb-6"
        >
          Something went wrong while getting your company ready for the
          conversation. Check your connection and try again.
        </Text>
        <AnimatedPressable
          onPress={() => queryClient.invalidateQueries({ queryKey: founderStartupKeys.mine })}
          className="px-6 py-3 rounded-xl flex-row items-center gap-2"
          style={{ backgroundColor: colors.primary[600] }}
        >
          <RefreshCw size={18} color={colors.text.inverse} />
          <Text style={{ color: colors.text.inverse }} className="font-semibold">
            Try Again
          </Text>
        </AnimatedPressable>
      </InterviewStateFrame>
    );
  }

  // No startup → the interview needs a real startup row to score against.
  if (!startupId) {
    return (
      <InterviewStateFrame>
        <View
          className="w-20 h-20 rounded-full items-center justify-center mb-4"
          style={{ backgroundColor: colors.amber.light }}
        >
          <AlertCircle size={40} color={colors.warning} />
        </View>
        <Text style={{ color: colors.text.primary }} className="text-xl font-bold text-center mb-2">
          Complete Your Profile
        </Text>
        <Text
          style={{ color: colors.text.tertiary }}
          className="text-sm text-center leading-relaxed mb-6"
        >
          Before we talk through your startup, add a few basics about your
          company so the conversation is grounded in the right context.
        </Text>
        <AnimatedPressable
          onPress={() => router.push('/(startup)/company')}
          className="px-6 py-3 rounded-xl"
          style={{ backgroundColor: colors.primary[600] }}
        >
          <Text style={{ color: colors.text.inverse }} className="font-semibold">
            Complete Profile
          </Text>
        </AnimatedPressable>
      </InterviewStateFrame>
    );
  }

  return (
    // Title lives in the container's own header row (Task 1.2-R) so the exit
    // controls sit with the interview state — the shell chrome stays unlabeled
    // to avoid a duplicate title on web.
    <AppLayout>
      <View className="flex-1" style={{ backgroundColor: colors.surface.primary }}>
        {/* Top-only safe area — the Composer owns its own bottom inset so
            KeyboardLayout can lift it cleanly without double-padding (mic/input
            no longer float up; buttons keep their bottom safe area). */}
        <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1">
          <View className="flex-1">
            <InterviewContainer
              startupId={startupId}
              startupName={startupName}
              title="Tell us about your startup"
              onClose={handleClose}
            />
          </View>
        </SafeAreaView>
      </View>
    </AppLayout>
  );
}
